import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Cookies from "js-cookie";
import { getCookieDomain } from "../utils/getCookieDomain";

import {
    APP_NS,
    I18N_NAMESPACES,
    Language,
    LANGUAGE_COOKIE,
    SUPPORTED_LANGUAGES,
    normalizeLanguage,
    resources,
    toHtmlLang,
    getLanguageDirection
} from "./config";
import { getPickerLanguages, normalizePickerLanguage } from "./picker-languages";

export {
    APP_NS,
    Language,
    LANGUAGE_COOKIE,
    SUPPORTED_LANGUAGES,
    normalizeLanguage,
    getPickerLanguages,
    normalizePickerLanguage
};

/**
 * Read the `lang` query param from the current URL (e.g. ?lang=es) so a page
 * can be opened directly in a given language. Highest-priority source; also
 * persisted to the cookie so it survives client-side navigation.
 */
const detectLanguageFromUrl = (): string | null => {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get("lang");
        // Any supported locale, not just the ones in the picker: this is how a
        // locale under test (pt_br) is opened.
        return normalizeLanguage(fromUrl);
    } catch {
        return null;
    }
};

/**
 * Custom language detection (instead of i18next-browser-languagedetector):
 * URL ?lang= -> cookie i18nextLng -> navigator.language -> fallback en.
 * Called on mount in Providers (not in init) so the first render matches the
 * server one (en) and there is no hydration mismatch.
 */
export const detectInitialLanguage = (): string => {
    if (typeof window === "undefined") {
        return Language.EN;
    }
    const fromUrl = detectLanguageFromUrl();
    if (fromUrl) {
        // Persist so the choice sticks after the param drops off on navigation.
        persistLanguage(fromUrl);
        return fromUrl;
    }
    // Explicit choice (or a QA-set cookie) wins for any supported locale.
    const fromCookie = normalizeLanguage(Cookies.get(LANGUAGE_COOKIE));
    if (fromCookie) {
        // Rewritten on the root domain: a host-only cookie (the funnels wrote one
        // until they moved to the root domain) never reaches the truegate `t.`
        // subdomain, whose renew page then fell back to English and LTR.
        persistLanguage(fromCookie);
        return fromCookie;
    }
    // Implicit detection stays limited to the picker list, so a locale still
    // under test is never handed to a browser that merely asks for it.
    const fromNavigator = normalizePickerLanguage(window.navigator?.language);
    if (fromNavigator) return fromNavigator;
    return Language.EN;
};

/**
 * Persist the selected language in a cookie (one year). Used by the language picker.
 * Scoped to the root domain, like the auth token, so the choice follows the user onto
 * the truegate checkout subdomain (`t.`): host-only it never reached the renew paywall
 * there, which fell back to the browser language. The host-only cookie earlier builds
 * wrote is removed first: the browser would list it ahead of the shared one, so the
 * stale value would keep winning for a year.
 */
export const persistLanguage = (lng: string): void => {
    const normalized = normalizeLanguage(lng) ?? Language.EN;
    const domain = getCookieDomain();
    Cookies.remove(LANGUAGE_COOKIE, { path: "/" });
    Cookies.set(LANGUAGE_COOKIE, normalized, { expires: 365, path: "/", ...(domain ? { domain } : {}) });
};

/**
 * Switch the app language. Always persist BEFORE i18n.changeLanguage: the latter
 * emits `languageChanged` synchronously (resources are inline, so i18next never
 * defers), and the listeners it wakes up — see app/Providers.tsx — trigger the
 * refetch of localized content, which resolves its locale from this very cookie.
 * Changing the language first would make those requests read the previous value
 * and leave content one language behind.
 */
export const setLanguage = (lng: string): void => {
    persistLanguage(lng);
    i18n.changeLanguage(lng);
};

i18n.use(initReactI18next).init({
    resources,
    // Silence the i18next/Locize promo notice printed via console.info on init.
    showSupportNotice: false,
    // Deterministic start on en (matches SSR). The real language is applied
    // on mount in Providers via applyDetectedLanguage().
    lng: Language.EN,
    fallbackLng: Language.EN,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: [...I18N_NAMESPACES],
    defaultNS: APP_NS,
    interpolation: {
        escapeValue: false
    },
    react: {
        useSuspense: false
    }
});

/**
 * Mirror the active language onto `<html lang>`, so assistive tech and crawlers
 * see the language the page is actually rendered in. The server always renders
 * `lang="en"` — the same deterministic start as the instance above, so hydration
 * still matches — and the detected value lands on mount via the `languageChanged`
 * that applyDetectedLanguage() emits, then on every later switch.
 * `dir` follows the language too (RTL_LANGUAGES in config.ts); the root layout's
 * inline script already set it from the cookie before first paint, so on mount
 * this only changes anything for a language detected from ?lang= or navigator.
 */
const applyHtmlLanguage = (lng: string): void => {
    document.documentElement.lang = toHtmlLang(lng);
    document.documentElement.dir = getLanguageDirection(lng);
};

i18n.on("languageChanged", (lng: string) => {
    if (typeof document === "undefined") return;
    applyHtmlLanguage(lng);
});

/**
 * Apply the detected language (cookie/navigator) after mount on the client.
 * Call from Providers to avoid a hydration mismatch.
 */
export const applyDetectedLanguage = (): void => {
    const detected = detectInitialLanguage();
    if (detected !== i18n.language) {
        i18n.changeLanguage(detected);
    } else {
        // No languageChanged when the detected language is the initial `en`, yet
        // <html> may still carry the inline script's dir — keep it in sync.
        applyHtmlLanguage(detected);
    }
};

export default i18n;
